import React, { useMemo, useRef, useState } from "react";
import {
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { Close as CloseIcon, PictureAsPdf as PdfIcon } from "@mui/icons-material";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { globalFormatCurrencyBRL, globalMaskDocument } from "../../../shared/utils";

interface ModalDetailSerasaResponseProps {
	isOpenModal: boolean;
	isClose: () => void;
	data: any;
	pdfRef?: React.RefObject<HTMLDivElement>; // ✅ novo prop
}

const ModalDetailSerasaResponse: React.FC<ModalDetailSerasaResponseProps> = ({ isOpenModal, isClose, data, pdfRef }) => {
	const internalPdfRef = useRef<HTMLDivElement>(null);
	const contentRef = pdfRef ?? internalPdfRef;
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

	// Verifique se o objeto data e a propriedade serasaResponse existem antes de continuar
	if (!data || !data.serasaResponse) {
		return null; // Retorna nulo se não houver dados disponíveis
	}

	const serasaData = useMemo(() => {
		try {
			if (
				typeof data.serasaResponse === "string" &&
				(data.serasaResponse.startsWith("{") || data.serasaResponse.startsWith("["))
			) {
				return JSON.parse(data.serasaResponse);
			}

			console.warn("serasaResponse não é um JSON válido:", data.serasaResponse);
			return { message: data.serasaResponse };
		} catch (error) {
			console.error("Erro ao fazer o parse do JSON serasaResponse:", error);
			return {};
		}
	}, [data.serasaResponse]);

	const serasaDetails = {
		CPF: globalMaskDocument(data.document),
		Nome: data.name,
		Política: serasaData.data?.["DV-Product"]?.Product[0]?.TipoSubProduto2 || "Não informado",
		Decisão: serasaData.data?.["DV-Application"]?.NomeRecomendacaoProposta || "Não informado",
		"QTDE pendências PEFIN": serasaData.data?.["DV-Serasa"]?.SerasaBureau[0]?.AnotacoesFinanceirasPF[0]?.Pefin?.QuantidadeTotalPefin || 0,
		"Valor pendências PEFIN": globalFormatCurrencyBRL(
			serasaData.data?.["DV-Serasa"]?.SerasaBureau[0]?.AnotacoesFinanceirasPF[0]?.Pefin?.ValorTotalPefin || 0
		),
		"QTDE pendências REFIN": serasaData.data?.["DV-Serasa"]?.SerasaBureau[0]?.AnotacoesFinanceirasPF[0]?.Refin?.QuantidadeTotalRefin || 0,
		"Valor pendências REFIN": globalFormatCurrencyBRL(
			serasaData.data?.["DV-Serasa"]?.SerasaBureau[0]?.AnotacoesFinanceirasPF[0]?.Refin?.ValorTotalRefin || 0
		),
		"Quantidade de Protestos": serasaData.data?.["DV-Serasa"]?.SerasaBureau[0]?.AnotacoesFinanceirasPF[0]?.Acao?.ValorTotalAcao || 0,
		"Total Cheque sem Fundo": serasaData.data?.["DV-Serasa"]?.SerasaBureau[0]?.AnotacoesFinanceirasPF[0]?.ChequeSemFundos?.ValorTotalCCF || 0,
		"Situação CPF": serasaData.data?.["DV-Serasa"]?.SerasaBureau[0]?.InformacoesPessoais?.Identificacao?.NomeSituacaoCPF || "Não informado",
		"Nascimento e Idade": `${new Date(data.birthDate).toLocaleDateString("pt-BR")} (${new Date().getFullYear() - new Date(data.birthDate).getFullYear()
			} anos)`,
		Score: serasaData.data?.["DV-Serasa"]?.SerasaBureau[0]?.Modelos?.Score?.SerasaScore || 0,
		"Aluguel Encargos Solicitado": globalFormatCurrencyBRL(serasaData.data?.["DV-Application"]?.ProdutoSolicitado?.ValorEmprestimoSolicitado || 0),
		"Comprometimento de Renda": serasaData.data?.["DV-Application"]?.DadosSaidaPersonalizados[7]?.Valor || 0,
		"Renda Presumida": globalFormatCurrencyBRL(serasaData.data?.["DV-Serasa"]?.SerasaBureau[0]?.Modelos?.RendaPresumida?.RendaPresumida || 0),
		"Limite Sugerido": globalFormatCurrencyBRL(serasaData.data?.["DV-Application"]?.DadosSaidaPersonalizados[9]?.Valor || 0),
		"Código Recomendação": serasaData.data?.["DV-Results"]?.MergedResults[0]?.ResultadoDecisao?.CodigoRecomendacao?.join(", ") || "Não informado",
		"Tabela Decisão": serasaData.data?.["DV-Results"]?.MergedResults[0]?.ResultadoDecisao?.TabelaDecisao?.join(", ") || "Não informado",
	};

	const handleGeneratePdf = async () => {
		if (!contentRef.current) return;

		setIsGeneratingPdf(true);
		try {
			const canvas = await html2canvas(contentRef.current, {
				scale: 2,
				useCORS: true,
				backgroundColor: "#ffffff",
			});

			const imgData = canvas.toDataURL("image/png");
			const pdf = new jsPDF("p", "mm", "a4");

			const pageWidth = pdf.internal.pageSize.getWidth();
			const pageHeight = pdf.internal.pageSize.getHeight();
			const imgWidth = pageWidth;
			const imgHeight = (canvas.height * imgWidth) / canvas.width;

			const headerTopY = 10;
			const headerTitleY = 14;
			const headerSubtitleY = 19;
			const contentTopY = 26;
			const bottomMargin = 10;
			const contentHeightPerPage = pageHeight - contentTopY - bottomMargin;

			const generatedAt = new Date().toLocaleString("pt-BR");
			const headerTitle = "Detalhes do Crédito";
			const headerSubtitleParts: string[] = [];
			if (data?.name) headerSubtitleParts.push(`Locatário: ${data.name}`);
			headerSubtitleParts.push(`Gerado em: ${generatedAt}`);
			const headerSubtitle = headerSubtitleParts.join("  |  ");

			const addHeader = () => {
				pdf.setTextColor(33);
				pdf.setFontSize(14);
				pdf.text(headerTitle, 10, headerTitleY);
				pdf.setFontSize(9);
				pdf.text(headerSubtitle, 10, headerSubtitleY, { maxWidth: pageWidth - 20 });
				pdf.setDrawColor(210);
				pdf.line(10, contentTopY - 4, pageWidth - 10, contentTopY - 4);
			};

			let heightLeft = imgHeight;
			let position = 0;

			addHeader();
			pdf.addImage(imgData, "PNG", 0, contentTopY + position, imgWidth, imgHeight);
			heightLeft -= contentHeightPerPage;

			while (heightLeft > 0) {
				position -= contentHeightPerPage;
				pdf.addPage();
				addHeader();
				pdf.addImage(imgData, "PNG", 0, contentTopY + position, imgWidth, imgHeight);
				heightLeft -= contentHeightPerPage;
			}

			const safeDocument = String(data.document ?? "").replace(/\D/g, "");
			const fileName = `detalhes-credito${safeDocument ? `-${safeDocument}` : ""}.pdf`;
			pdf.save(fileName);
		} finally {
			setIsGeneratingPdf(false);
		}
	};

	return (
		<Dialog open={isOpenModal} onClose={isClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ pb: 1 }}>
				<Box display="flex" alignItems="center" justifyContent="space-between" pr={0.5}>
					<Box>
						<Typography variant="h6" fontWeight={600} color="primary.main">
							Detalhes do Crédito
						</Typography>
					</Box>

					<Box display="flex" gap={0.5}>
						<Tooltip title="Gerar PDF">
							<span>
								<IconButton onClick={handleGeneratePdf} disabled={isGeneratingPdf}>
									{isGeneratingPdf ? <CircularProgress size={20} /> : <PdfIcon />}
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Fechar">
							<IconButton onClick={isClose}>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
			</DialogTitle>
			<Divider />
			<DialogContent>
				<Box ref={contentRef} sx={{ p: 1 }}>
					<TableContainer component={Paper}>
						<Table size="small">
							<TableBody>
								{Object.entries(serasaDetails).map(([key, value]) => {
									if (key === "Valor pendências REFIN" || key === "Valor pendências PEFIN") {
										return null;
									}
									if (key === "QTDE pendências REFIN") {
										return (
											<TableRow key={key}>
												<TableCell component="th" scope="row">
													Pendências REFIN
												</TableCell>
												<TableCell>{`${serasaDetails["QTDE pendências REFIN"]} pendências (REFIN): ${serasaDetails["Valor pendências REFIN"]}`}</TableCell>
											</TableRow>
										);
									}
									if (key === "QTDE pendências PEFIN") {
										return (
											<TableRow key={key}>
												<TableCell component="th" scope="row">
													Pendências PEFIN
												</TableCell>
												<TableCell>{`${serasaDetails["QTDE pendências PEFIN"]} pendências (PEFIN): ${serasaDetails["Valor pendências PEFIN"]}`}</TableCell>
											</TableRow>
										);
									}

									return (
										<TableRow key={key}>
											<TableCell component="th" scope="row">
												{key}
											</TableCell>
											<TableCell
												sx={{
													color:
														key === "Decisão" && value === "DECLINADA"
															? "red"
															: key === "Decisão"
																? "green"
																: "inherit",
											}}
										>
											{value}
										</TableCell>
									</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default ModalDetailSerasaResponse;
